import React, {useCallback, useState} from "react";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {Client} from "@/types/userManagement";
import {ClientSelectV2} from "@/lib/clients/ClientSelectV2";
import {useAuth} from "@/context/user";

type IncidentFeedFormProps = {
    mode: "create" | "edit";
    initialData?: {
        name: string;
        client?: Client;
        type: string;
        reference: string;
        isDefault?: boolean;
    };
    onSubmit: (data: {
        clientID?: string;
        name: string;
        type: string;
        reference: string;
        isDefault: boolean;
    }) => Promise<void>;
    onCancel: () => void;
}

export function IncidentFeedForm({mode, initialData, onSubmit, onCancel}: IncidentFeedFormProps) {
    const {isSuperAdmin} = useAuth();
    const [client, setClient] = useState<Client | undefined>(initialData?.client);
    const [name, setName] = useState(initialData?.name || "");
    const [type, setType] = useState(initialData?.type || "samdesk");
    const [reference, setReference] = useState(initialData?.reference || "");
    const [isDefault, setIsDefault] = useState(initialData?.isDefault || false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            alert("Please enter a name");
            return;
        }

        if (!reference.trim()) {
            alert("Please enter a reference");
            return;
        }

        setLoading(true);
        try {
            await onSubmit({
                clientID: client?.id,
                name: name.trim(),
                type,
                reference: reference.trim(),
                isDefault,
            });
        } finally {
            setLoading(false);
        }
    }, [client, name, type, reference, isDefault, onSubmit]);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-2xl bg-white shadow p-6">
            <h1 className="text-2xl font-semibold">
                {mode === "create" ? "Create Incident Feed" : "Edit Incident Feed"}
            </h1>

            {isSuperAdmin && (
                <div className="flex flex-col gap-2">
                    <ClientSelectV2
                        label="Client"
                        required={false}
                        onChange={setClient}
                        initialID={client?.id}
                    />
                    <p className="text-sm text-grey">
                        Leave empty to make this feed available globally to all clients
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-2">
                <label className="font-medium">
                    Name <span className="text-dark-red">*</span>
                </label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border border-light-grey rounded px-3 py-2"
                    required
                />
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">
                    Type <span className="text-dark-red">*</span>
                </label>
                <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="border border-light-grey rounded px-3 py-2 bg-white"
                    required
                >
                    <option value="samdesk">Samdesk</option>
                    <option value="intelligence_fusion">Intelligence Fusion</option>
                </select>
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">
                    Reference <span className="text-dark-red">*</span>
                </label>
                <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="border border-light-grey rounded px-3 py-2"
                    required
                />
            </div>

            <div className="border border-light-light-grey rounded p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                        className="w-4 h-4 accent-blue"
                    />
                    <span className="font-medium">Set as Default Feed</span>
                </label>
                <p className="text-sm text-grey mt-2 ml-7">
                    Default feeds will be automatically selected for new alerts
                </p>
            </div>

            <div className="flex gap-4">
                <ButtonV2
                    label="Cancel"
                    onClick={onCancel}
                    color="gray"
                    type="button"
                />
                <ButtonV2
                    label={mode === "create" ? "Create" : "Update"}
                    type="submit"
                    disabled={loading}
                />
            </div>
        </form>
    );
}
